const air = {
  mode: "air",
  emoji: "\u2708\uFE0F",
  title: "Air Travel",
  subtitle: "Airlines, Airports and the World of Flight",
  linkTo: "/air",
  linkLabel: "Air Travel",
  motto: "Explore \u2022 Plan \u2022 Share \u2022 Travel",
  teaser: [
    "Air travel connects people, cities, countries and continents faster than any other major form of transportation. For many travelers, the journey begins at an airport, continues with one or more airlines, and ends with a connection to trains, buses, taxis, rideshare, rental cars, hotels, cruise ports or another flight.",
  ],
  raw: `
Air travel connects people, cities, countries and continents faster than any other major form of transportation.
For many travelers, the journey begins at an airport, continues with one or more airlines, and ends with a connection to trains, buses, taxis, rideshare, rental cars, hotels, cruise ports or another flight.
TravelerPulse.pro brings the world of aviation together in one connected place.
The AIR section is designed to help travelers explore:
Airlines
Airports
Terminals
Airline hubs
Routes
Destinations
Lounges
Airport transportation
Airport hotels
Parking
Dining
Shopping
Traveler services
Passport and visa information
Immigration and customs
Airline and airport news
Traveler reviews and experiences

## ✈️ One Global Air Travel Directory
TravelerPulse.pro aims to create a comprehensive worldwide aviation directory organized by:
World → Continent → Country → State/Province → City → Airport → Terminal → Airline → Route
Travelers should be able to begin with almost any aviation-related question and move naturally through the system.
For example:
Which airports serve this city?
Which airlines use this airport?
Where does this airline fly?
Which terminal does my airline use?
How do I get from the airport to downtown?
What hotels are near the airport?
Do I need a transit visa?
TravelerPulse.pro is designed to connect these questions rather than treating them as separate searches.

## 🛫 Airlines
Every airline can have its own detailed TravelerPulse.pro profile.
Possible information includes:
Airline name
Airline code
ICAO code
IATA code
Country
Headquarters
Parent company
Alliance
Main hubs
Secondary hubs
Focus cities
Destinations
Fleet
Aircraft types
Cabin classes
Baggage rules
Check-in information
Loyalty program
Lounge access
Accessibility
Customer service
Official website
Reservation links
Mobile app
Traveler reviews
Airline news

## 🌎 Airlines by Country
Every country profile can include:
Airlines Based in This Country
Categories may include:
National carriers
Full-service airlines
Low-cost airlines
Regional airlines
Charter airlines
Cargo airlines where relevant
Domestic carriers
Travelers can also search:
Airlines Flying to This Country
This connects the airline directory directly with destination planning.

## 🛩 Airline Hubs
Create profiles and links for airline hubs.
Each airline profile can identify:
Primary hubs
Secondary hubs
Focus cities
Each airport profile can identify:
Airlines Using This Airport as a Hub
This helps travelers understand major connection points.

## 🗺 Airline Routes
Route pages can connect:
Origin Airport → Airline → Destination Airport
Possible information includes:
Airline
Departure airport
Arrival airport
Route distance
Typical flight duration
Aircraft types commonly used
Seasonal service
Nonstop or connecting service
Terminal information
Codeshare information
Because schedules change frequently, current route and schedule information should be verified from reliable sources.

## 🤝 Airline Alliances
TravelerPulse.pro can organize airlines by major alliance and partnership relationships.
Profiles may include:
Alliance membership
Codeshare partners
Frequent-flyer partnerships
Lounge-sharing arrangements
Connecting airlines
This is particularly useful for travelers planning complex international trips.

## 💺 Cabin Classes
Airline profiles can explain the classes each carrier offers, such as:
Economy
Premium Economy
Business
First Class
Regional Business
Basic Economy
Extra-legroom seating
Information can include:
Seat features
Baggage allowance
Meals
Lounge access
Priority services
Wi-Fi
Entertainment

## 🧳 Airline Baggage
Baggage rules can be confusing because they vary by airline, route, fare and cabin.
TravelerPulse.pro should link travelers to official airline information covering:
Carry-on bags
Personal items
Checked baggage
Weight limits
Size limits
Additional bags
Oversized luggage
Sports equipment
Musical instruments
Strollers
Wheelchairs
Pets
Baggage fees
Official airline policies should always receive priority.

## ✅ Airline Check-In
Airline profiles can provide links and information concerning:
Online check-in
Mobile check-in
Airport check-in
Check-in deadlines
Bag-drop deadlines
Boarding deadlines
Travel-document verification
Where available, link directly to the airline's official check-in system.

## 🎟 Tickets, Fares & Reservations
TravelerPulse.pro can help users understand common fare concepts including:
One-way fares
Round-trip fares
Multi-city fares
Flexible fares
Refundable fares
Non-refundable fares
Award tickets
Upgrade options
Basic economy restrictions
TravelerPulse.pro should not represent itself as the ticketing airline unless booking capability is specifically offered.

## 🔄 Connections & Layovers
Connecting flights are an important part of international travel.
Airport and route profiles can provide information about:
Minimum connection concepts
Terminal transfers
Airside connections
Re-clearing security
Immigration
Customs
Baggage recheck
Airport trains
Shuttle buses
Walking between terminals
Connection procedures can vary by airport, airline and itinerary.

## 🛂 Transit Visas
Some international connections may require special visa or transit documentation.
TravelerPulse.pro can connect aviation pages directly with Passport, Visa, Embassy & Consulate Information.
Travelers can access official resources concerning:
Transit visas
Airside transit
Passport validity
Visa requirements
Immigration rules
Entry requirements
Customs
Because these rules can change, users should always verify them with official government authorities.

## 🏢 Airports
Every major airport can have a comprehensive TravelerPulse.pro profile.
Possible fields include:
Airport name
Airport code
ICAO code
City served
State/province
Country
Address
Coordinates
Official website
Telephone
Terminals
Runways
Airlines
Routes
Passenger services
Ground transportation
Parking
Rental cars
Hotels
Lounges
Restaurants
Shops
Accessibility
Wi-Fi
Traveler reviews
News

## 🌍 Airports by Country
Each country profile can display:
Airports in This Country
Organize airports by:
Major international airports
Regional airports
Domestic airports
Island airports
Remote airports
State or province pages can also list airports within their region.

## 🏙 Airports by City
Many major cities have more than one airport.
TravelerPulse.pro can provide:
Airports Serving This City
For each airport, show:
Distance from city
Airlines
Major destinations
Ground transportation
Rail connection
Taxi
Rideshare
Airport bus
Hotels
This can help travelers compare airports before booking.

## 🧭 Airport Terminals
Each airport terminal can have its own profile.
Possible information includes:
Terminal number/name
Airlines
Gates
Check-in
Security
Immigration
Customs
Lounges
Dining
Shopping
Ground transportation
Terminal transfers
Accessibility
This is especially important at large international airports.

## 🔁 Terminal Transfers
TravelerPulse.pro can explain how passengers move between terminals.
Possible methods include:
Walking
Moving walkways
Airport train
Shuttle bus
Airside transfer
Landside transfer
Where appropriate, identify whether passengers may need to:
Exit security
Re-clear security
Pass through immigration
Recheck luggage

## 🛬 Arrivals
Airport profiles can include an Arriving at This Airport section with information about:
Immigration
Passport control
Customs
Baggage claim
Ground transportation
Taxi
Rideshare
Train
Bus
Rental cars
Hotel shuttles
Meeting points
This helps travelers understand what happens after landing.

## 🛫 Departures
Create a Departing From This Airport section with information about:
Check-in areas
Bag drop
Security
Passport control
Departure gates
Lounges
Dining
Shopping
Boarding

## 🛂 Immigration & Passport Control
International airport profiles can link to official information concerning:
Immigration
Passport control
Entry requirements
Automated gates
Electronic arrival forms
Visas
Transit procedures
TravelerPulse.pro should not invent immigration rules.
Always prioritize official government sources.

## 🛄 Customs
Airport pages can link travelers to official customs information covering:
Duty-free allowances
Currency declarations
Food restrictions
Agricultural products
Medication
Restricted goods
Prohibited items

## 🧳 Baggage Claim
Airport terminal profiles can identify:
Baggage claim areas
Oversized baggage
Baggage carts
Lost baggage
Airline baggage service desks
TravelerPulse.pro can link travelers to the relevant airline's lost-baggage process.

## ❓ Lost & Found
Clearly separate Airline Lost Baggage from Airport Lost & Found.
A suitcase not delivered after a flight may be handled by the airline, while an item left inside a terminal may be handled by the airport.
Provide official links whenever possible.

## 🚆 Airport Rail
Airport profiles should connect directly with the RAIL section.
Show:
Airport express train
Metro
Subway
Commuter rail
Intercity rail
Station location
Terminal access
Connections

## 🚌 Airport Bus
Link airports with the BUS section.
Include:
Airport buses
City buses
Express coaches
Regional buses
Intercity buses
Hotel shuttles

## 🚕 Airport Taxi & Ground Transportation
Every airport should have a Taxi & Ground Transportation section covering:
Taxi stands
Prepaid taxis
Private transfers
Limousines
Shared vans
Hotel shuttles
Rental cars

## 📍 Airport Rideshare
Create a Rideshare Pickup & Drop-Off section with:
Authorized companies
Pickup zones
Terminal levels
Parking-garage pickup
Designated lots
Airport surcharges
Official airport instructions

## 🚗 Rental Cars
Airport profiles can identify:
Rental-car companies
Rental-car center
Terminal desks
Shuttle buses
Pickup procedures
Return locations
This connects AIR with the ROAD section.

## 🅿️ Airport Parking
Create comprehensive airport parking information.
Possible categories include:
Short-term parking
Long-term parking
Economy parking
Valet
Garage parking
Surface lots
Cell-phone waiting lots
Accessible parking
EV charging
Off-airport parking

## 🏨 Airport Hotels
Every major airport can connect with the STAY section.
Organize:
Terminal hotels
Airport hotels
Nearby hotels
Hotels with shuttle
Hotels with parking packages

## 🍽 Airport Dining
Large airport terminals often contain dozens or even hundreds of food options.
Create a Restaurants & Dining section organized by:
Terminal
Concourse
Gate area
Before security
After security
Cuisine
Opening hours
Categories may include:
Restaurants
Cafés
Bakeries
Bars
Fast food
Grab-and-go
Food courts

## 🛍 Airport Shopping
TravelerPulse.pro can identify:
Duty-free
Convenience stores
Luxury retail
Electronics
Books
Gifts
Local products
Pharmacies
Travel essentials

## 🛋 Airport Lounges
Create detailed profiles for:
Airline lounges
Alliance lounges
Independent lounges
Premium credit-card lounges
Pay-per-use lounges
Possible information includes:
Terminal
Location
Hours
Access rules
Eligible airlines
Membership programs
Food
Drinks
Wi-Fi
Showers
Family areas
Business facilities
Because access policies change, official lounge sources should be linked.

## ♿ Accessible Air Travel
TravelerPulse.pro should make accessibility information easy to find.
Airline and airport profiles can connect users to:
Wheelchair assistance
Mobility assistance
Accessible restrooms
Elevators
Accessible parking
Accessible transportation
Service-animal policies
Assistance for visual impairment
Assistance for hearing impairment
Special boarding
Passengers requiring assistance should be directed to official airline and airport resources.

## 👨‍👩‍👧 Flying With Children
Information may include:
Family boarding
Strollers
Car seats
Bassinets
Children's meals
Unaccompanied minors
Family restrooms
Nursing rooms
Play areas
Policies vary by airline.

## 👴 Air Travel for Seniors
TravelerPulse.pro can highlight practical information for older travelers including:
Wheelchair assistance
Airport carts
Shorter walking options
Elevators
Accessible restrooms
Lounges
Priority services where offered
Ground transportation
Airport hotels

## 🐕 Pets & Service Animals
Airline profiles can provide official resources covering:
Pets in cabin
Checked pets
Cargo animals
Service animals
Carriers
Documentation
Destination restrictions
International animal transportation can involve government requirements and should be linked to official authorities.

## 🌐 Wi-Fi & Connectivity
Airport and airline profiles can include:
Airport Wi-Fi
Airline onboard Wi-Fi
Charging stations
SIM card services
eSIM information
Mobile connectivity

## 🍽 Food in the Air
Airline profiles may include general information about:
Meals
Snacks
Special meals
Vegetarian meals
Vegan meals
Religious meals
Allergy requests
Buy-on-board service
Official airline information should receive priority.

## 🎬 Inflight Entertainment
Possible airline profile information includes:
Seatback screens
Streaming entertainment
Movies
Television
Music
Games
Wi-Fi entertainment

## 💺 Aircraft Profiles
TravelerPulse.pro can also connect airlines to the aircraft they operate.
Aircraft profiles may include:
Manufacturer
Model
Typical seating
Range
Cabin configuration
Airline operators
Travelers interested in aviation can move naturally between airline, fleet and route information.

## ⏱ Delays & Cancellations
Air travel can be disrupted by:
Weather
Mechanical issues
Crew problems
Air traffic control
Airport congestion
Security issues
Strikes
Schedule changes
TravelerPulse.pro can help users locate official airline and airport resources regarding:
Flight status
Rebooking
Refunds
Cancellations
Customer service
Passenger rights

## ⚠️ When Things Go Wrong
TravelerPulse.pro can provide a place for travelers to share experiences involving:
Flight cancellations
Major delays
Baggage problems
Missed connections
Customer service
Seat problems
Airport experiences
Accessibility
Refund disputes
The goal should be constructive discussion and useful information.

## ⭐ Airline Reviews
Travelers can share experiences concerning:
Booking
Check-in
Boarding
Seating
Cabin comfort
Meals
Service
Wi-Fi
Entertainment
Baggage
Customer support
Overall experience
Reviews should remain clearly separate from official airline information.

## ⭐ Airport Reviews
Travelers can review airports based on:
Navigation
Cleanliness
Security
Immigration
Dining
Shopping
Lounges
Ground transportation
Accessibility
Connections
Overall experience

## 💬 Airline & Airport Communities
Every airline and major airport can have its own TravelerPulse.pro discussion area.
Travelers can:
Ask questions
Share tips
Discuss terminals
Discuss connections
Share lounge information
Post photographs
Report changes
Share baggage experiences
Discuss accessibility
Share complaints
Help other travelers

## 📸 Aviation Photos
TravelerPulse.pro members can share:
Aircraft photos
Airport photos
Terminal photos
Airline cabins
Lounges
Airport architecture
Runway views
Travel experiences
Photos can be tagged and connected to the relevant airport, airline, aircraft and destination.

## ✍️ Air Travel Stories
Travel stories may include:
Memorable flights
First flights
Long-haul journeys
Unique airports
Difficult connections
Unexpected upgrades
Airline experiences
Aviation history
Airport adventures
These stories help transform aviation information into a global traveler community.

## 📰 Airline & Airport News
TravelerPulse.pro can create dedicated news sections for:
Airlines
Airports
New routes
New terminals
Airline launches
Airline closures
Fleet orders
Airport expansion
Lounge openings
Aviation technology
Passenger regulations
Travel disruptions
News should automatically connect to relevant airline, airport, city and country profiles.

## 🧭 My Travel Hub
Air travel can be a major part of My Travel Hub.
Travelers may save:
Flights
Airlines
Airports
Terminals
Hotels
Lounges
Transportation
Visa information
Embassy contacts
Travel notes
Favorite airports
Favorite airlines
Photos
Travel stories
A complete journey might look like:
Home → Rideshare → Airport → Airline → Connecting Airport → Airline → Destination Airport → Train → Hotel
TravelerPulse.pro connects each stage.

## 🔎 Intelligent Air Travel Search
TravelerPulse.pro can eventually support searches such as:
Airports near me
Airlines flying from Seattle to India
Which airlines fly to Dubai?
Hotels near an airport
Airport lounges in Terminal 1
Train from airport to downtown
Rideshare pickup at airport
Which terminal does my airline use?
Transit visa for an airport connection
Restaurants near my departure gate
Search should connect aviation with the rest of the TravelerPulse network.

## 🔗 Connect AIR Throughout TravelerPulse.pro
Every relevant profile should contain useful aviation connections.
Country
Airports & Airlines
State/Province
Airports
City
Airports Serving This City
Airport
Airlines • Terminals • Ground Transportation • Hotels
Airline
Hubs • Airports • Routes
Hotel
Nearest Airport
Cruise Port
Nearest Airports
Railway Station
Airport Connections
Destination
How to Get Here by Air
This creates an interconnected aviation network instead of isolated listings.

## 🌐 A Connected Global Aviation Community
The real strength of TravelerPulse.pro is not simply listing airlines and airports.
It is connecting air travel with the complete journey.
An airline connects airports.
An airport connects cities.
Cities connect to hotels and restaurants.
Airports connect to trains, buses, taxis and rideshare.
Cruise ports connect to airports.
Countries connect to passport and visa information.
Routes connect travelers to new destinations.
And travelers connect with one another.

## ✈️ AIR on TravelerPulse.pro
From a small regional airport to one of the world's largest international hubs — from a short domestic flight to a journey across continents — TravelerPulse.pro aims to bring airlines, airports and air travel together within one global travel community.
Find the airport. Discover the airline. Plan the connection. Explore the destination. Share the journey.
`,
};
export default air;
